import React from 'react';
import { AngularServicesContext } from 'react-app';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { patchCourse as patchJourney } from 'redux/actions/courses';
import moment from 'moment';
import NvDropdown, {
  NvDropdownOption,
  NvDropdownButtonStyle,
} from 'shared/components/inputs/nv-dropdown';
import NvTooltip from 'shared/components/nv-tooltip';
import AutomatedTranslationMenu from 'course_home/components/automated-translation-menu';
import { Institution } from 'redux/schemas/models/institution';
import t from 'react-translate';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { config } from '../../../../config/pendo.config.json';
import { NavigationHeaderOrigin } from './navigation-header';

const NavigationRightElement = ({
  origin,
  rightRef,
  catalogId,
  scrolledHeader,
  isFontColorLight,
  hasCredlyIntegration,
  handleCredlyBadgeRef,
}) => {
  const currentJourney = useSelector((state) => state.models.courses[catalogId]);
  const storedCatalogId = useSelector((state) => state.app.currentCatalogId);
  const currentInstitution: Institution = useSelector(getCurrentInstitution);
  const currentInstitutionId = useSelector((state) => state.app.currentInstitutionId);
  const dispatch = useAppDispatch();

  const {
    $scope,
    $state,
    RailsRoutes,
    CurrentPermissionsManager,
  } = React.useContext(AngularServicesContext);

  const formattedReleaseDate = currentJourney?.releaseDate
    ? moment(currentJourney.releaseDate).format('L')
    : null;
  const showAutomatedTranslationMenu = storedCatalogId && currentInstitution.automatedTranslationEnabled && currentJourney?.automatedTranslationEnabled;

  const updateJourneyLanguage = ({ contentLanguage, automatedTranslationEnabled }) => dispatch(patchJourney({
    catalogId,
    contentLanguage,
    id: currentJourney.id,
    institutionId: currentInstitutionId,
    currentLanguage: currentJourney.contentLanguage,
    automatedTranslationEnabled,
  }));

  // Function to build the option items for the menu
  function getOptionItems() {
    const pendo = config.pendo.journeyAnalytics;
    const updatedOptionItems = [];

    const optionsHasItem = (id) => updatedOptionItems.find(x => x.id === id);

    if (currentJourney) {
      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isConfigAndRegistrationRole())
        && !optionsHasItem('edit-basics')
      ) {
        updatedOptionItems.push({
          id: 'edit-basics',
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.EDIT_BASICS(),
          callback: () => $state.go('learning-journey-basics', {
            catalogId,
            institutionId: currentInstitutionId,
          }),
          dataQa: `${pendo.editBasics}_cog`,
        });
      }

      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
          || CurrentPermissionsManager.isCourseBuilder())
        && !optionsHasItem('edit-journey')
      ) {
        updatedOptionItems.push({
          id: 'edit-journey',
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.EDIT_JOURNEY(),
          callback: () => $state.go('edit-learning-journey', {
            catalogId,
            institutionId: currentInstitutionId,
          }),
          dataQa: `${pendo.editJourney}_cog`,
        });
      }

      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isConfigAndRegistrationRole()
        || CurrentPermissionsManager.isCourseBuilder())
        && !optionsHasItem('welcome-email')
      ) {
        updatedOptionItems.push({
          id: 'welcome-email',
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.JOURNEY_COMMUNICATIONS(),
          callback: () => $state.go('learning-journey-communications', {
            catalogId,
            institutionId: currentInstitutionId,
          }),
          dataQa: pendo.welcomeEmail,
        });
      }

      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isConfigAndRegistrationRole()
        || CurrentPermissionsManager.isCourseBuilder())
        && !optionsHasItem('journey-language')
      ) {
        updatedOptionItems.push({
          id: 'journey-language',
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.JOURNEY_LANGUAGE(),
          dataQa: `${pendo.journeyLanguage}_cog`,
          callback: () => {
            $scope.$uibModal.open({
              templateUrl: 'shared/templates/language-modal.html',
              controller: 'LanguageModalCtrl as vm',
              resolve: {
                currentUser: null,
                context: {
                  isJourney: true,
                  updateJourneyLanguage,
                  currentLanguage: currentJourney.contentLanguage,
                },
              },
            });
          },
        });
      }
      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isConfigAndRegistrationRole()
        || CurrentPermissionsManager.isCourseBuilder()
        || CurrentPermissionsManager.isInstructor())
        && !optionsHasItem('completion-email')
      ) {
        updatedOptionItems.push({
          id: 'completion-email',
          type: 'link',
          targetBlank: true,
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.COMPLETION_EMAIL(),
          link: RailsRoutes.courseAdminDashboardTabPath(catalogId, 'statements'),
          dataQa: `${pendo.completionEmail}_cog`,
        });
      }

      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isConfigAndRegistrationRole()
        || CurrentPermissionsManager.isCourseBuilder())
        && !optionsHasItem('certificate-of-completion')
      ) {
        updatedOptionItems.push({
          id: 'certificate-of-completion',
          type: 'link',
          targetBlank: true,
          link: RailsRoutes.courseAdminDashboardTabPath(catalogId, 'statements'),
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.CERTIFICATE_OF_COMPLETION(),
          dataQa: `${pendo.certificateOfCompletion}_cog`,
        });
      }

      if (
        CurrentPermissionsManager.isCourseBuilder() && hasCredlyIntegration && !optionsHasItem('credly-badge')
      ) {
        updatedOptionItems.push({
          id: 'credly-badge',
          dataQa: config.pendo.credly.journeyMenuItem,
          type: 'text',
          text: t.COURSE_ADMIN_MEGA_MENU.CREDLY_BADGE.LABEL(),
          callback: () => handleCredlyBadgeRef.current(),
        });
      }
      if (updatedOptionItems.length > 0) {
        updatedOptionItems.push({ type: 'divider' });
      }
      if (
        (CurrentPermissionsManager.hasCourseManagerPermissions()
        || CurrentPermissionsManager.isLearnerRegistrationRole()
        || CurrentPermissionsManager.hasFullCourseAdminPermissions())
        && !optionsHasItem('user-management')
      ) {
        updatedOptionItems.push({
          id: 'user-management',
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.USER_MANAGEMENT(),
          callback: () => $state.go('learning-journey-user-management-learners', { catalogId, from: (origin === NavigationHeaderOrigin.HOME ? 'learning-journey-home' : undefined) }),
          dataQa: `${pendo.userManagement}_cog`,
        });
      }
      if (CurrentPermissionsManager.hasJourneyAnalyticsAccess()
      ) {
        updatedOptionItems.push({
          type: 'text',
          text: t.LEARNING_JOURNEYS.DASHBOARD.JOURNEY_OPTIONS.ANALYTICS(),
          callback: () => $state.go('learning-journey-analytics', { catalogId, institutionId: currentInstitution.id, from: 'learning-journey-home' }),
          dataQa: `${pendo.analytics}_cog`,
        });
      }
    }

    return updatedOptionItems;
  }

  const optionItems: NvDropdownOption[] = getOptionItems();

  return (
    <div
      ref={rightRef}
      className={`right${scrolledHeader ? ' scrolled' : ''}`}
    >
      {!!optionItems.length && (
        <React.Fragment>
          {formattedReleaseDate && (
            <div className='release-info font-weight-bold mr-2 page-title-xxs'>
              {t.LEARNING_JOURNEYS.DETAILS[currentJourney?.released ? 'PAST_RELEASE' : 'FUTURE_RELEASE'](formattedReleaseDate)}
            </div>
          )}
          <NvTooltip placement='auto' text={t.DASHBOARD.ADMIN_OPTIONS_TOOLTIP()} enabled={origin === NavigationHeaderOrigin.USER_MANAGEMENT}>
            <div className='dropdown-container'>
              <NvDropdown
                useEffectFlushFix
                items={optionItems}
                iconClass={`icon-small icon-settings${origin === NavigationHeaderOrigin.USER_MANAGEMENT ? ' hoverable' : ''}`}
                buttonStyle={NvDropdownButtonStyle.ICON}
                toggleDataQa={config.pendo.learningJourneys.settings}
              />
            </div>
          </NvTooltip>
        </React.Fragment>
      )}
      {
        showAutomatedTranslationMenu
        && (
          <div className='d-flex'>
            {
              !!optionItems.length
              && (
                <div className={`vertical-bar bottom-inline-block ${isFontColorLight ? 'text-white' : ''}`} />
              )
            }
            <AutomatedTranslationMenu />
          </div>
        )
      }
    </div>
  );
};

export default NavigationRightElement;
